import { useEffect, useState, useCallback } from "react";
import { Accessibility, X, Plus, Minus, RotateCcw, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

type Settings = {
  fontScale: number;
  contrast: "none" | "high" | "inverted";
  grayscale: boolean;
  highlightLinks: boolean;
  readableFont: boolean;
  stopAnimations: boolean;
  bigCursor: boolean;
};

const DEFAULTS: Settings = {
  fontScale: 1,
  contrast: "none",
  grayscale: false,
  highlightLinks: false,
  readableFont: false,
  stopAnimations: false,
  bigCursor: false,
};

const STORAGE_KEY = "a11y-settings-v1";

const apply = (s: Settings) => {
  const root = document.documentElement;
  root.style.setProperty("--a11y-font-scale", String(s.fontScale));
  root.classList.toggle("a11y-contrast-high", s.contrast === "high");
  root.classList.toggle("a11y-contrast-inverted", s.contrast === "inverted");
  root.classList.toggle("a11y-grayscale", s.grayscale);
  root.classList.toggle("a11y-highlight-links", s.highlightLinks);
  root.classList.toggle("a11y-readable-font", s.readableFont);
  root.classList.toggle("a11y-stop-animations", s.stopAnimations);
  root.classList.toggle("a11y-big-cursor", s.bigCursor);
};

export const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<Settings>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULTS;
  });

  useEffect(() => {
    apply(settings);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
  }, [settings]);

  const update = useCallback(<K extends keyof Settings>(k: K, v: Settings[K]) => {
    setSettings(prev => ({ ...prev, [k]: v }));
  }, []);

  const reset = () => setSettings(DEFAULTS);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="פתח תפריט נגישות"
        aria-haspopup="dialog"
        className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-40 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary text-primary-foreground border-2 border-accent flex items-center justify-center shadow-elegant hover:scale-110 transition-smooth"
        style={{ marginBottom: 0 }}
      >
        <Accessibility className="w-6 h-6 sm:w-7 sm:h-7" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="תפריט נגישות"
          className="fixed inset-0 z-[100] flex items-end sm:items-center sm:justify-start"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="relative bg-background w-full sm:w-[380px] sm:m-6 sm:rounded-xl shadow-elegant max-h-[90vh] overflow-y-auto border border-border">
            <div className="sticky top-0 bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between sm:rounded-t-xl">
              <div className="flex items-center gap-2">
                <Accessibility className="w-5 h-5 text-accent" />
                <h2 className="font-heading font-bold text-base">תפריט נגישות</h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="סגור תפריט נגישות"
                className="p-1 rounded hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-5 text-sm">
              <section>
                <h3 className="font-bold mb-2">גודל טקסט</h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => update("fontScale", Math.max(0.8, +(settings.fontScale - 0.1).toFixed(2)))}
                    aria-label="הקטן טקסט"
                    className="w-10 h-10 rounded-md border border-border flex items-center justify-center hover:bg-secondary"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="flex-1 text-center font-medium" aria-live="polite">
                    {Math.round(settings.fontScale * 100)}%
                  </div>
                  <button
                    type="button"
                    onClick={() => update("fontScale", Math.min(1.6, +(settings.fontScale + 0.1).toFixed(2)))}
                    aria-label="הגדל טקסט"
                    className="w-10 h-10 rounded-md border border-border flex items-center justify-center hover:bg-secondary"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </section>

              <section>
                <h3 className="font-bold mb-2">ניגודיות וצבעים</h3>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { key: "none", label: "רגיל" },
                    { key: "high", label: "ניגודיות גבוהה" },
                    { key: "inverted", label: "צבעים הפוכים" },
                  ] as const).map(o => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => update("contrast", o.key)}
                      aria-pressed={settings.contrast === o.key}
                      className={cn(
                        "px-2 py-2 rounded-md border text-xs font-medium transition-smooth",
                        settings.contrast === o.key
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border hover:bg-secondary"
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="font-bold mb-1">אפשרויות נוספות</h3>
                {([
                  { key: "grayscale", label: "גווני אפור" },
                  { key: "highlightLinks", label: "הדגשת קישורים" },
                  { key: "readableFont", label: "גופן קריא יותר" },
                  { key: "stopAnimations", label: "עצירת אנימציות" },
                  { key: "bigCursor", label: "סמן עכבר גדול" },
                ] as const).map(o => (
                  <label key={o.key} className="flex items-center justify-between gap-3 p-2 rounded-md hover:bg-secondary cursor-pointer">
                    <span>{o.label}</span>
                    <input
                      type="checkbox"
                      checked={settings[o.key] as boolean}
                      onChange={(e) => update(o.key, e.target.checked as never)}
                      className="w-5 h-5 accent-[hsl(var(--accent))]"
                      aria-label={o.label}
                    />
                  </label>
                ))}
              </section>

              <section className="flex flex-col gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={reset}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-md border border-border hover:bg-secondary font-medium"
                >
                  <RotateCcw className="w-4 h-4" /> איפוס הגדרות
                </button>
                <Link
                  to="/accessibility"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
                >
                  <LinkIcon className="w-4 h-4" /> הצהרת נגישות מלאה
                </Link>
              </section>

              <p className="text-xs text-muted-foreground leading-relaxed pt-2 border-t border-border">
                רכז נגישות: מג׳די קאסם · טל׳ <a href="tel:043005117" dir="ltr" className="underline">04-3005117</a> · <a href="mailto:magdeq@gelovo.dev" className="underline">magdeq@gelovo.dev</a>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
